import { api } from './apiClient';
import type { Brand, BrandAlias, ListResponse, SingleResponse } from '../types/domain';

export interface BrandInput {
  name?: string;
  slug?: string;
  logo_url?: string | null;
  sort_order?: number;
  is_active?: boolean;
}

export interface BrandAliasInput {
  brand_id?: number;
  pattern?: string;
  match_type?: 'exact' | 'contains';
  priority?: number;
  is_active?: boolean;
}

export interface UnmappedBrand {
  raw_value: string;
  product_count: number;
}

// Mutations return the affected row plus how many products were re-materialised.
interface MutationResponse<T> {
  data: T;
  reapplied?: number;
}

export const brandService = {
  // ── Brands ──────────────────────────────────────────────────────────────
  async list(): Promise<Brand[]> {
    const res = await api.get<ListResponse<Brand>>('/api/v1/brands');
    return res.data;
  },

  async get(id: number): Promise<Brand> {
    const res = await api.get<SingleResponse<Brand>>(`/api/v1/brands/${id}`);
    return res.data;
  },

  // Distinct raw brand values that no active alias maps yet.
  async listUnmapped(): Promise<UnmappedBrand[]> {
    const res = await api.get<{ data: UnmappedBrand[] }>('/api/v1/brands/unmapped');
    return res.data;
  },

  async create(data: BrandInput): Promise<Brand> {
    const res = await api.post<MutationResponse<Brand>>('/api/v1/brands', data);
    return res.data;
  },

  async update(id: number, data: BrandInput): Promise<MutationResponse<Brand>> {
    return api.put<MutationResponse<Brand>>(`/api/v1/brands/${id}`, data);
  },

  async remove(id: number): Promise<MutationResponse<Brand>> {
    return api.delete<MutationResponse<Brand>>(`/api/v1/brands/${id}`);
  },

  // Re-run the materialisation of products.brand_name_cleaned from the aliases.
  async apply(): Promise<number> {
    const res = await api.post<{ data: { reapplied: number } }>('/api/v1/brands/apply');
    return res.data.reapplied;
  },

  // ── Aliases ─────────────────────────────────────────────────────────────
  async listAliases(brandId?: number): Promise<BrandAlias[]> {
    const res = await api.get<ListResponse<BrandAlias>>(
      '/api/v1/brand-aliases',
      brandId ? { brand_id: brandId } : undefined
    );
    return res.data;
  },

  async createAlias(data: BrandAliasInput): Promise<MutationResponse<BrandAlias>> {
    return api.post<MutationResponse<BrandAlias>>('/api/v1/brand-aliases', data);
  },

  async updateAlias(id: number, data: BrandAliasInput): Promise<MutationResponse<BrandAlias>> {
    return api.put<MutationResponse<BrandAlias>>(`/api/v1/brand-aliases/${id}`, data);
  },

  async removeAlias(id: number): Promise<MutationResponse<BrandAlias>> {
    return api.delete<MutationResponse<BrandAlias>>(`/api/v1/brand-aliases/${id}`);
  },
};
